import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SEO from "../SEO";
import { SalesCar } from "../../types/types";
import { salesCarsApi } from "../../lib/supabase";

const formatPrice = (price: number) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(price);

const formatMileage = (mileage: number) =>
  new Intl.NumberFormat("en-GB").format(mileage);

const statusLabel = (status: SalesCar["status"]) => {
  switch (status) {
    case "available":
      return "Available";
    case "reserved":
      return "Reserved";
    case "sold":
      return "Sold";
    default:
      return status;
  }
};

const statusColor = (status: SalesCar["status"]) => {
  switch (status) {
    case "available":
      return "success";
    case "reserved":
      return "warning";
    case "sold":
      return "default";
    default:
      return "default";
  }
};

const SalesPage: React.FC = () => {
  const navigate = useNavigate();
  const [cars, setCars] = useState<SalesCar[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [make, setMake] = useState("all");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    const loadCars = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await salesCarsApi.getAll();
        setCars(data || []);
      } catch (err: any) {
        setError(`Failed to load sales inventory: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    loadCars();
  }, []);

  const makes = useMemo(() => {
    const uniqueMakes = new Set(cars.map((car) => car.make));
    return ["all", ...Array.from(uniqueMakes).sort()];
  }, [cars]);

  const filteredCars = useMemo(() => {
    const searchValue = search.trim().toLowerCase();
    let results = cars.filter((car) => {
      const matchesSearch =
        !searchValue ||
        `${car.year} ${car.make} ${car.model}`
          .toLowerCase()
          .includes(searchValue);
      const matchesMake = make === "all" || car.make === make;
      const matchesStatus = status === "all" || car.status === status;
      return matchesSearch && matchesMake && matchesStatus;
    });

    switch (sort) {
      case "price-low":
        results = [...results].sort((a, b) => a.price - b.price);
        break;
      case "price-high":
        results = [...results].sort((a, b) => b.price - a.price);
        break;
      case "year-new":
        results = [...results].sort((a, b) => b.year - a.year);
        break;
      case "year-old":
        results = [...results].sort((a, b) => a.year - b.year);
        break;
      case "mileage-low":
        results = [...results].sort((a, b) => a.mileage - b.mileage);
        break;
      case "featured":
      default:
        results = [...results].sort((a, b) => {
          const featuredDiff = Number(b.featured) - Number(a.featured);
          if (featuredDiff !== 0) return featuredDiff;
          return b.year - a.year;
        });
        break;
    }

    return results;
  }, [cars, make, search, sort, status]);

  return (
    <>
      <SEO
        title="Sales | JP Performance Cars"
        description="Curated internal sales inventory with a premium, concierge-led buying experience."
        canonical="https://www.jpperformancecars.co.uk/sales"
      />
      <Box
        sx={{
          minHeight: "calc(100vh - 160px)", // Ensure content takes up space
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            mt: { xs: "110px", md: "255px" }, // Fixed spacing: mobile 90px + 20px, desktop 205px + 50px
            py: { xs: 4, md: 6 },
            flex: 1,
            pb: { xs: 8, md: 12 }, // Extra bottom padding to push footer down
          }}
        >
          <Stack spacing={3}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Search by model or year"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Make</InputLabel>
                  <Select
                    label="Make"
                    value={make}
                    onChange={(event) => setMake(event.target.value)}
                    MenuProps={{
                      disableScrollLock: true,
                    }}
                  >
                    {makes.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item === "all" ? "All Makes" : item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={status}
                    onChange={(event) => setStatus(event.target.value)}
                    MenuProps={{
                      disableScrollLock: true,
                    }}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value="available">Available</MenuItem>
                    <MenuItem value="reserved">Reserved</MenuItem>
                    <MenuItem value="sold">Sold</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Sort</InputLabel>
                  <Select
                    label="Sort"
                    value={sort}
                    onChange={(event) => setSort(event.target.value)}
                    MenuProps={{
                      disableScrollLock: true,
                    }}
                  >
                    <MenuItem value="featured">Featured</MenuItem>
                    <MenuItem value="price-low">Price (Low to High)</MenuItem>
                    <MenuItem value="price-high">Price (High to Low)</MenuItem>
                    <MenuItem value="year-new">Newest Year</MenuItem>
                    <MenuItem value="year-old">Oldest Year</MenuItem>
                    <MenuItem value="mileage-low">Lowest Mileage</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Grid container spacing={3}>
              {isLoading && filteredCars.length === 0 ? (
                <Grid item xs={12}>
                  <Box
                    sx={{ py: 6, display: "flex", justifyContent: "center" }}
                  >
                    <CircularProgress sx={{ color: "#DC143C" }} />
                  </Box>
                </Grid>
              ) : (
                filteredCars.map((car) => {
                  const primaryImage =
                    car.images?.[car.primary_image_index ?? 0] ?? "";
                  return (
                    <Grid item xs={12} sm={6} md={4} key={car.id}>
                      <Card
                        sx={{
                          borderRadius: 3,
                          border: "1px solid #e5e7eb",
                          boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                          height: "100%",
                        }}
                      >
                        <CardActionArea
                          onClick={() => navigate(`/sales/${car.slug}`)}
                        >
                          <Box
                            sx={{
                              height: 220,
                              backgroundImage: primaryImage
                                ? `url(${primaryImage})`
                                : "linear-gradient(135deg, #0f172a, #1f2937)",
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {!primaryImage && (
                              <Typography
                                variant="body2"
                                sx={{ color: "rgba(255,255,255,0.7)" }}
                              >
                                Image coming soon
                              </Typography>
                            )}
                          </Box>
                          <CardContent sx={{ p: 3 }}>
                            <Stack spacing={1.5}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                              >
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                >
                                  {car.year}
                                </Typography>
                                <Chip
                                  label={statusLabel(car.status)}
                                  color={statusColor(car.status) as any}
                                  size="small"
                                />
                              </Stack>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {car.make} {car.model}
                              </Typography>
                              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                {formatPrice(car.price)}
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                              >
                                <Chip
                                  label={`${formatMileage(car.mileage)} mi`}
                                  size="small"
                                />
                                {car.transmission && (
                                  <Chip label={car.transmission} size="small" />
                                )}
                                {car.fuel && (
                                  <Chip label={car.fuel} size="small" />
                                )}
                              </Stack>
                            </Stack>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })
              )}
            </Grid>

            {!isLoading && filteredCars.length === 0 && (
              <Box
                sx={{
                  textAlign: "center",
                  py: 6,
                  borderRadius: 3,
                  border: "1px dashed #e5e7eb",
                }}
              >
                <Typography variant="h6" sx={{ mb: 1 }}>
                  No vehicles match your filters.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try adjusting your search or clearing filters.
                </Typography>
                <Button
                  variant="outlined"
                  sx={{ mt: 2 }}
                  onClick={() => {
                    setSearch("");
                    setMake("all");
                    setStatus("all");
                    setSort("featured");
                  }}
                >
                  Reset filters
                </Button>
              </Box>
            )}
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default SalesPage;
